import { Trans } from '@lingui/react/macro';
import { createFileRoute, Outlet, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/_auth')({
  component: AuthLayout,
});

function AuthLayout() {
  return (
    <div className="min-h-screen flex">
      {/* Left panel - Branding & illustration */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary to-primary/80" />

        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="1.5" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Floating decorative elements */}
        <div className="absolute top-20 left-16 w-32 h-32 bg-white/10 rounded-3xl rotate-12 animate-pulse" />
        <div
          className="absolute bottom-32 right-20 w-40 h-40 bg-white/10 rounded-full"
          style={{ animationDelay: '1s' }}
        />
        <div className="absolute top-1/3 right-1/4 w-24 h-24 bg-white/5 rounded-2xl -rotate-12" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 text-white w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm transition-transform group-hover:scale-105">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-7 w-7"
              >
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                <path d="M3 6h18" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
            </div>
            <span className="text-2xl font-heading font-bold">TrackMyGroceries</span>
          </Link>

          {/* Main message */}
          <div className="max-w-lg">
            <h1 className="font-display text-4xl xl:text-5xl font-semibold leading-tight mb-6">
              <Trans>Know what you have.</Trans>
              <br />
              <span className="text-white/80">
                <Trans>Waste less. Save more.</Trans>
              </span>
            </h1>
            <p className="text-lg xl:text-xl text-white/70 leading-relaxed">
              <Trans>
                Track your food inventory effortlessly. Get smart expiry alerts, reduce waste, and
                never forget what&apos;s in your pantry again.
              </Trans>
            </p>
          </div>

          {/* Stats or testimonial */}
          <div className="flex items-center gap-8">
            <div>
              <div className="text-3xl font-bold font-heading">40%</div>
              <div className="text-sm text-white/60">
                <Trans>Up to 40% less food waste</Trans>
              </div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div>
              <div className="text-3xl font-bold font-heading">$200+</div>
              <div className="text-sm text-white/60">
                <Trans>Up to $200 or more saved monthly</Trans>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - Auth forms */}
      <div className="flex-1 flex flex-col min-h-screen bg-background">
        {/* Mobile header */}
        <div className="lg:hidden p-6 pb-0">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-6 w-6"
              >
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                <path d="M3 6h18" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
            </div>
            <span className="text-xl font-heading font-bold">TrackMyGroceries</span>
          </Link>
        </div>

        {/* Form container - centered */}
        <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12">
          <div className="w-full max-w-[400px] animate-in">
            <Outlet />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 text-center text-sm text-muted-foreground">
          <p>
            <Trans>
              By continuing, you agree to our{' '}
              <Link
                to="/terms"
                className="text-foreground hover:text-primary underline-offset-4 hover:underline"
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                to="/privacy"
                className="text-foreground hover:text-primary underline-offset-4 hover:underline"
              >
                Privacy Policy
              </Link>
            </Trans>
          </p>
        </div>
      </div>
    </div>
  );
}
